/**
 * Offline presenting store (v2.1 B14/D06), IndexedDB database `xcreative-offline`:
 * - `bundles`: OfflineBundleMeta per design (what was stored, for which saved revision)
 * - `files`:   bytes per `${designId}|${key}` (scene JSON, font faces, assets, font catalog)
 *
 * Bundles are always built from the server's offline manifest of a SAVED revision
 * (`GET /designs/{d}/offline-manifest?revision=`) and the scene is downloaded from its
 * `sceneUrl` — never from unsaved editor state. Every file is checked against the manifest's
 * sha256 (and size for assets); failures are recorded, and readiness then says not ready.
 *
 * This is the ONLY place /v1 API data is cached for offline use; the service worker never
 * caches /v1 responses.
 */
import type { Scene } from '@xcreative/scene-core';
import { ApiClient, type FetchLike } from '../api/client.js';
import type { FontInfo, OfflineManifest } from '../api/types.js';
import { sha256Hex } from '../lib/hash.js';
import { assetKey, fontKey, SCENE_KEY, urlPath, type OfflineBundleMeta } from './readiness.js';

const DB_NAME = 'xcreative-offline';
const CATALOG_KEY = 'font-catalog';

let dbp: Promise<IDBDatabase> | null = null;

function db(): Promise<IDBDatabase> {
  if (dbp) return dbp;
  dbp = new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('เบราว์เซอร์นี้ไม่รองรับการเก็บข้อมูลออฟไลน์ (IndexedDB)'));
      return;
    }
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => {
      if (!req.result.objectStoreNames.contains('bundles')) req.result.createObjectStore('bundles');
      if (!req.result.objectStoreNames.contains('files')) req.result.createObjectStore('files');
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error('เปิดพื้นที่เก็บข้อมูลออฟไลน์ไม่ได้'));
  });
  dbp.catch(() => (dbp = null));
  return dbp;
}

function run<T>(store: 'bundles' | 'files', mode: IDBTransactionMode, fn: (s: IDBObjectStore) => IDBRequest<T> | void): Promise<T | undefined> {
  return db().then(
    (d) =>
      new Promise<T | undefined>((resolve, reject) => {
        const t = d.transaction(store, mode);
        const req = fn(t.objectStore(store));
        let result: T | undefined;
        if (req) req.onsuccess = () => (result = req.result);
        t.oncomplete = () => resolve(result);
        t.onerror = () => reject(t.error ?? new Error('IndexedDB error'));
        t.onabort = () => reject(t.error ?? new Error('พื้นที่เก็บข้อมูลในเครื่องอาจเต็ม'));
      }),
  );
}

const fileId = (designId: string, key: string) => `${designId}|${key}`;

export async function loadBundleMeta(designId: string): Promise<OfflineBundleMeta | null> {
  try {
    return (await run<OfflineBundleMeta>('bundles', 'readonly', (s) => s.get(designId) as IDBRequest<OfflineBundleMeta>)) ?? null;
  } catch {
    return null;
  }
}

export async function readFile(designId: string, key: string): Promise<ArrayBuffer | null> {
  try {
    return (await run<ArrayBuffer>('files', 'readonly', (s) => s.get(fileId(designId, key)) as IDBRequest<ArrayBuffer>)) ?? null;
  } catch {
    return null;
  }
}

async function writeFile(designId: string, key: string, bytes: ArrayBuffer): Promise<void> {
  await run('files', 'readwrite', (s) => void s.put(bytes, fileId(designId, key)));
}

export async function deleteBundle(designId: string): Promise<void> {
  await run('bundles', 'readwrite', (s) => void s.delete(designId));
  await run('files', 'readwrite', (s) => void s.delete(IDBKeyRange.bound(`${designId}|`, `${designId}|￿`)));
}

/** Logout clears offline copies too (like the recovery journal). */
export async function clearOfflineBundles(): Promise<void> {
  try {
    await run('bundles', 'readwrite', (s) => void s.clear());
    await run('files', 'readwrite', (s) => void s.clear());
  } catch {
    /* nothing stored */
  }
}

export interface PrepareProgress {
  done: number;
  total: number;
  label: string;
}

/**
 * Download everything the manifest lists and store it. Resolves with the stored meta even
 * when some files failed (readiness then lists them); rejects only when the manifest or the
 * scene cannot be obtained at all.
 */
export async function prepareOffline(client: ApiClient, designId: string, revision: number, onProgress?: (p: PrepareProgress) => void): Promise<OfflineBundleMeta> {
  const manifest: OfflineManifest = await client.offlineManifest(designId, revision);
  const total = 1 + manifest.fonts.length + manifest.assets.length;
  let done = 0;
  const tick = (label: string) => onProgress?.({ done: ++done, total, label });

  // Replace any previous copy of this design.
  await deleteBundle(designId);

  const meta: OfflineBundleMeta = { designId, revision: manifest.revision, savedAt: 0, manifest, sceneStored: false, files: {}, failures: {} };

  const sceneBytes = await client.fetchBytes(manifest.sceneUrl);
  const scene = JSON.parse(new TextDecoder().decode(sceneBytes)) as Scene;
  if (!scene || !Array.isArray(scene.pages) || !scene.pages.length) throw new Error('ข้อมูลสไลด์จากเซิร์ฟเวอร์ไม่ถูกต้อง');
  await writeFile(designId, SCENE_KEY, sceneBytes);
  meta.sceneStored = true;
  meta.files[SCENE_KEY] = { sha256: await sha256Hex(sceneBytes), byteSize: sceneBytes.byteLength };
  tick('ข้อมูลสไลด์');

  // Font catalog (public) so the offline font manager knows the faces; synthesized if unavailable.
  try {
    const catalog = await client.fonts();
    await writeFile(designId, CATALOG_KEY, new TextEncoder().encode(JSON.stringify(catalog)).buffer as ArrayBuffer);
  } catch {
    await writeFile(designId, CATALOG_KEY, new TextEncoder().encode(JSON.stringify(synthCatalog(manifest))).buffer as ArrayBuffer);
  }

  const jobs: { key: string; url: string; sha256: string; byteSize: number | null; label: string }[] = [
    ...manifest.fonts.map((f) => ({ key: fontKey(f.url), url: f.url, sha256: f.sha256, byteSize: null, label: 'ฟอนต์' })),
    ...manifest.assets.map((a) => ({ key: assetKey(a.id), url: a.url, sha256: a.sha256, byteSize: a.byteSize, label: 'รูปภาพ' })),
  ];
  const queue = [...jobs];
  const worker = async () => {
    for (let job = queue.shift(); job; job = queue.shift()) {
      try {
        const bytes = await client.fetchBytes(job.url);
        const sha = await sha256Hex(bytes);
        if (sha.toLowerCase() !== job.sha256.toLowerCase()) throw new Error('ข้อมูลไม่ตรงกับ sha256 ที่เซิร์ฟเวอร์ระบุ');
        if (job.byteSize !== null && bytes.byteLength !== job.byteSize) throw new Error(`ขนาดไม่ตรง (${bytes.byteLength} ≠ ${job.byteSize} ไบต์)`);
        await writeFile(designId, job.key, bytes);
        meta.files[job.key] = { sha256: sha, byteSize: bytes.byteLength };
      } catch (e) {
        meta.failures[job.key] = e instanceof Error ? e.message : 'ดาวน์โหลดไม่สำเร็จ';
      }
      tick(job.label);
    }
  };
  await Promise.all([worker(), worker(), worker(), worker()]);

  meta.savedAt = Date.now();
  await run('bundles', 'readwrite', (s) => void s.put(meta, designId));
  return meta;
}

function synthCatalog(manifest: OfflineManifest): { items: FontInfo[] } {
  const byId = new Map<string, FontInfo>();
  for (const f of manifest.fonts) {
    const m = /\/fonts\/([^/]+)\/(\d+)\/(normal|italic)/.exec(urlPath(f.url));
    if (!m) continue;
    const info = byId.get(m[1]!) ?? { id: m[1]!, family: 'ฟอนต์ออฟไลน์', license: '', faces: [] };
    info.faces.push({ weight: Number(m[2]), italic: m[3] === 'italic', sha256: f.sha256, url: f.url });
    byId.set(m[1]!, info);
  }
  return { items: [...byId.values()] };
}

export interface OfflineBundle {
  meta: OfflineBundleMeta;
  scene: Scene;
  /** API client that serves fonts, assets and the font catalog from the stored copy first. */
  client: ApiClient;
}

/** Open the stored copy of a design (null when there is none or it is unreadable). */
export async function openBundle(designId: string): Promise<OfflineBundle | null> {
  const meta = await loadBundleMeta(designId);
  if (!meta || !meta.sceneStored) return null;
  const sceneBytes = await readFile(designId, SCENE_KEY);
  if (!sceneBytes) return null;
  let scene: Scene;
  try {
    scene = JSON.parse(new TextDecoder().decode(sceneBytes)) as Scene;
  } catch {
    return null;
  }
  const cachedFetch: FetchLike = async (input, init) => {
    const path = urlPath(input);
    const method = (init?.method ?? 'GET').toUpperCase();
    if (method === 'GET') {
      let key: string | null = null;
      const asset = /\/assets\/([^/]+)\/content$/.exec(path);
      const face = /\/fonts\/([^/]+\/\d+\/(?:normal|italic))$/.exec(path);
      if (asset) key = assetKey(decodeURIComponent(asset[1]!));
      else if (face) {
        // The manifest may name faces with another URL form: match on the id/weight/style tail.
        const hit = meta.manifest.fonts.find((f) => urlPath(f.url).endsWith(`/${face[1]}`));
        key = fontKey(hit ? hit.url : path);
      } else if (/\/fonts$/.test(path)) key = CATALOG_KEY;
      if (key) {
        const bytes = await readFile(designId, key);
        if (bytes) return new Response(bytes, { status: 200, headers: key === CATALOG_KEY ? { 'Content-Type': 'application/json' } : {} });
      }
    }
    return globalThis.fetch(input, init);
  };
  return { meta, scene, client: new ApiClient({ fetch: cachedFetch }) };
}
